<template>
    <div class="authentication">
        <h4 class="pb-4">Mobile Verification Required</h4>
        <p>Please enter the 6-digit verification code sent to your mobile phone number <strong>{{ displayMobile }}</strong>.</p>

        <b-alert :show="true" variant="warning" v-if="errorMessage">{{ errorMessage }}</b-alert>
        <b-alert :show="true" variant="success" v-if="successMessage">{{ successMessage }}</b-alert>
        <b-alert :show="true" variant="danger" v-if="attemptsRemaining <= 0">
            Too many failed attempts. Please update your number or request a new code to reset your attempts.
        </b-alert>

        <b-overlay :show="loading">
            <!-- Code verification form -->
            <form @submit="verifyCode" class="mb-4">
                <b-form-group class="pb-2">
                    <b-input-group class="mb-3">
                        <b-input-group-prepend>
                            <b-input-group-text class="p-0 no-background">
                                <img alt="icon code" width="20" src="../../assets/auth/ic-lock.svg">
                            </b-input-group-text>
                        </b-input-group-prepend>
                        <b-input 
                            type="text" 
                            placeholder="6-DIGIT CODE" 
                            v-model="verificationCode" 
                            maxLength="6" 
                            :disabled="attemptsRemaining <= 0" 
                            aria-label="Verification Code" 
                        />
                    </b-input-group>
                </b-form-group>

                <b-row class="mb-3" v-if="attemptsRemaining > 0 && attemptsRemaining < 3">
                    <b-col class="text-muted small">
                        Attempts remaining: {{ attemptsRemaining }}
                    </b-col>
                </b-row>

                <b-row>
                    <b-col md="12" class="p-0">
                        <b-button 
                            variant="primary" 
                            type="submit" 
                            class="button-pill text-uppercase pl-4 pr-4 pt-2 pb-2" 
                            :disabled="attemptsRemaining <= 0 || verificationCode.length !== 6" 
                            block
                        >
                            Verify Code
                        </b-button>
                    </b-col>
                </b-row>
            </form>

            <!-- Resend link with countdown -->
            <b-row class="mb-3 text-center">
                <b-col>
                    <span v-if="resendCooldown > 0" class="text-muted small">
                        Didn't get the code? <span style="text-decoration: underline; color: grey;">Resend verification code here ({{ resendCooldown }} s)</span>
                    </span>
                    <a v-else href="#" @click.prevent="resendCode" class="small" style="text-decoration: underline;">
                        Didn't get the code? Resend verification code here
                    </a>
                </b-col>
            </b-row>

            <!-- Wrong number link -->
            <b-row class="mb-4 text-center" v-if="!showChangeMobile">
                <b-col>
                    <a href="#" @click.prevent="showChangeMobile = true" class="small" style="text-decoration: underline;">
                        Wrong number? Change phone number here
                    </a>
                </b-col>
            </b-row>

            <!-- Inline change phone number form -->
            <b-collapse v-model="showChangeMobile">
                <div class="card p-3 mb-4 bg-light">
                    <h6 class="mb-2">Update Phone Number</h6>
                    <b-form-group class="pb-2">
                        <b-input-group class="mb-3">
                            <b-input-group-prepend>
                                <b-input-group-text class="p-0 no-background">
                                    <img alt="icon mobile" width="20" src="../../assets/auth/ic-user.svg">
                                </b-input-group-text>
                            </b-input-group-prepend>
                            <b-input 
                                type="text" 
                                placeholder="NEW PHONE NUMBER" 
                                v-model="newMobile" 
                                aria-label="New Phone Number" 
                            />
                        </b-input-group>
                    </b-form-group>
                    <b-row>
                        <b-col md="6" class="pr-md-1 mb-2 mb-md-0">
                            <b-button 
                                variant="secondary" 
                                size="sm" 
                                @click="updateMobileNumber" 
                                :disabled="!newMobile" 
                                block
                            >
                                Update &amp; Resend
                            </b-button>
                        </b-col>
                        <b-col md="6" class="pl-md-1">
                            <b-button 
                                variant="outline-secondary" 
                                size="sm" 
                                @click="showChangeMobile = false" 
                                block
                            >
                                Cancel
                            </b-button>
                        </b-col>
                    </b-row>
                </div>
            </b-collapse>

            <b-row>
                <b-col md="12" class="text-center">
                    <a href="#" @click.prevent="goBackToLogin" class="small text-muted">Back to login</a>
                </b-col>
            </b-row>
        </b-overlay>
    </div>
</template>

<script>
    import Managers from '../../managers';

    export default {
        name: "VerifyMobile2FAV2",
        data() {
            return {
                email: '',
                password: '',
                token: '',
                displayMobile: '',
                newMobile: '',
                verificationCode: '',
                errorMessage: '',
                successMessage: '',
                attemptsRemaining: 3,
                resendCooldown: 0,
                cooldownTimer: null,
                showChangeMobile: false,
                loading: false
            }
        },
        methods: {
            startCooldown() {
                this.resendCooldown = 30;
                if (this.cooldownTimer) {
                    clearInterval(this.cooldownTimer);
                }
                this.cooldownTimer = setInterval(() => {
                    if (this.resendCooldown > 0) {
                        this.resendCooldown--;
                    } else {
                        clearInterval(this.cooldownTimer);
                    }
                }, 1000);
            },
            verifyCode($e) {
                if ($e) $e.preventDefault();
                this.errorMessage = '';
                this.successMessage = '';
                this.loading = true;

                this.$api.v2.post('auth/verify-mobile-2fa', {
                    verificationCode: this.verificationCode
                }).then((resp) => {
                    if (resp.body.emailConfirmed === false) {
                        // User verified mobile but email is not confirmed. Clear token and redirect to login
                        this.$store.dispatch('authentication/storeV2Token', { token: null });
                        this.$router.push({
                            name: 'login-v2',
                            params: {
                                message: 'Mobile number verified successfully. Please check your email for confirmation instructions.'
                            }
                        });
                    } else {
                        // Email is confirmed! Log in fully
                        this.loginAndRedirect();
                    }
                }).catch((err) => {
                    this.attemptsRemaining--;
                    this.errorMessage = this.extractError(err);
                    this.verificationCode = '';
                }).finally(() => {
                    this.loading = false;
                });
            },
            resendCode() {
                if (this.resendCooldown > 0) return;
                this.errorMessage = '';
                this.successMessage = '';
                this.loading = true;

                this.$api.v2.post('auth/resend-mobile-2fa')
                    .then((resp) => {
                        this.successMessage = resp.body.message || 'Verification code resent successfully.';
                        this.attemptsRemaining = 3; // Reset attempts on manual resend
                        this.startCooldown();
                    })
                    .catch((err) => {
                        this.errorMessage = this.extractError(err);
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },
            updateMobileNumber() {
                if (!this.newMobile) return;
                this.errorMessage = '';
                this.successMessage = '';
                this.loading = true;

                this.$api.v2.post('auth/update-mobile-2fa', {
                    mobile: this.newMobile
                }).then((resp) => {
                    this.successMessage = resp.body.message || 'Mobile number updated successfully.';
                    this.displayMobile = resp.body.mobile || this.newMobile;
                    this.attemptsRemaining = 3; // Reset attempts on successful change
                    this.showChangeMobile = false;
                    this.newMobile = '';
                    this.startCooldown();
                }).catch((err) => {
                    this.errorMessage = this.extractError(err);
                }).finally(() => {
                    this.loading = false;
                });
            },
            loginAndRedirect() {
                // Log in cleanly behind the scenes now that verification is complete
                this.loading = true;
                Managers.authentication.login(this.email, this.password).then(() => {
                    this.$router.push({ name: 'welcome' });
                }).catch((err) => {
                    // Fallback: if auto-login fails, redirect to login page with message
                    this.$store.dispatch('authentication/storeV2Token', { token: null });
                    this.$router.push({
                        name: 'login-v2',
                        params: { message: 'Mobile number verified successfully. Please log in with your credentials.' }
                    });
                }).finally(() => {
                    this.loading = false;
                });
            },
            goBackToLogin() {
                // Clear temporary token when returning to login
                this.$store.dispatch('authentication/storeV2Token', { token: null });
                this.$router.push({ name: 'login-v2' });
            },
            extractError(err) {
                if (err.body && err.body.errors) {
                    const keys = Object.keys(err.body.errors);
                    if (keys.length > 0) {
                        return err.body.errors[keys[0]][0];
                    }
                }
                return err.body?.message || err.body?.error || 'An error occurred. Please try again.';
            }
        },
        created() {
            // Retrieve params passed from LoginV2 redirect
            if (this.$route.params && this.$route.params.token) {
                this.email = this.$route.params.email;
                this.password = this.$route.params.password;
                this.token = this.$route.params.token;
                this.displayMobile = this.$route.params.mobile || '';
                
                // Store the temporary token so subsequent requests are authenticated
                this.$store.dispatch('authentication/storeV2Token', { token: this.token });
            } else {
                // If loaded without parameters (e.g. page refresh), redirect to login
                this.goBackToLogin();
            }
        },
        beforeDestroy() {
            if (this.cooldownTimer) {
                clearInterval(this.cooldownTimer);
            }
        }
    }
</script>

<style scoped>
    @import '../../assets/auth/auth.css';
</style>
